import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClockCheck } from '@tabler/icons-react';
import styles from './LastComputed.module.scss';
import { formatDateTime, formatRelative } from '../../Feedbacks/feedbackFormat';

/** How often the relative label re-renders, so "2 minutes ago" does not sit there going stale. */
const TICK_MS = 30_000;

interface LastComputedProps {
  /** ISO timestamp of the last recompute, or undefined if there has never been one. */
  at?: string;
  /**
   * Whether this describes one project or a whole portfolio - a portfolio quotes
   * its *oldest* value, and says so, which is a different sentence.
   */
  scope?: 'project' | 'portfolio';
}

/**
 * Says when the KPIs on screen were computed.
 *
 * The recompute runs on a queue rather than inside the write that triggered it,
 * so a page opened right after an edit can legitimately be showing numbers from
 * a moment before it. That is a fine trade as long as the page is honest about
 * it, which is this component's whole job.
 *
 * The exact timestamp is on the tooltip; the label is relative, because "3
 * minutes ago" answers "can I trust this?" and an absolute time does not.
 */
const LastComputed: React.FC<LastComputedProps> = ({ at, scope = 'project' }) => {
  const { t, i18n } = useTranslation('templates');

  // Re-render on a timer: nothing else changes, so without this the label would
  // keep claiming whatever it said when the data arrived.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!at) return undefined;
    const timer = window.setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => window.clearInterval(timer);
  }, [at]);

  const locale = i18n.language?.toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';

  if (!at) {
    return <span className={styles.stale}>{t('report.lastComputed.never')}</span>;
  }

  return (
    <span className={styles.wrapper} title={formatDateTime(at, locale)}>
      <IconClockCheck size={14} stroke={1.8} />
      {t(`report.lastComputed.${scope}`, { when: formatRelative(at, locale) })}
    </span>
  );
};

export default LastComputed;
